import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import type { CohortChartConfig } from '@/features/chartsView';
import {
  cohortChartDataConfigValidator,
  useRequestConfig,
} from '@/features/chartsView';
import type { DataStatus } from '@/shared/lib';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Loader } from '@/shared/ui';
import {
  getChartData,
  initMeta,
  selectMetaIsLoaded,
  selectMetaIsLoading,
} from '../../model/slice';
import type { CohortDataResponse } from '../../model/types';
import { Chart } from '../Chart/Chart';

interface Props {
  preset: unknown;
  optionColors?: ReadonlyMap<string, string>;
  onDataFetchFinish?: (data?: CohortDataResponse) => void;
  onDataStatusChange?: (dataStatus: DataStatus) => void;
}

export function Widget({
  preset,
  optionColors,
  onDataFetchFinish,
  onDataStatusChange,
}: Props) {
  const dispatch = useAppDispatch();
  const metaIsLoading = useAppSelector(selectMetaIsLoading);
  const metaIsLoaded = useAppSelector(selectMetaIsLoaded);
  const config = useMemo(() => {
    const parsed = cohortChartDataConfigValidator.safeParse(preset);

    return parsed.success ? parsed.data : (preset as CohortChartConfig);
  }, [preset]);

  const requestConfig = useRequestConfig(config);

  const onGetData = useCallback(
    () => {
      return dispatch(getChartData(config));
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requestConfig, dispatch],
  );

  useEffect(() => {
    dispatch(initMeta());
  }, [dispatch]);

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: 0,
        position: 'relative',
        width: '100%',
        display: 'flex',
      }}
    >
      <Loader active={metaIsLoading} />
      {metaIsLoaded && (
        <Chart
          config={config}
          optionColors={optionColors}
          onGetData={onGetData}
          onDataChange={onDataFetchFinish}
          onDataStatusChange={onDataStatusChange}
        />
      )}
    </Box>
  );
}
